import {css, StyleSheet} from 'aphrodite';
import reactUpdate from 'immutability-helper';
import PropTypes from 'prop-types';
import React from 'react';

import {assert} from 'Asserts';
import {PartJSON} from 'biojs/Part';
import {SchemaTag} from 'folder-items/FileConstants';
import HelpButton from 'help/HelpButton';
import TagValueEditor from 'metadata/TagValueEditor';
import TagValueViewer from 'metadata/TagValueViewer';
import * as SchemaFieldUtil from 'schemas/fields/SchemaFieldUtil';
import * as TagUtil from 'schemas/tags/TagUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import BaseEditableField from 'ui-library/BaseEditableField';
import * as Colors from 'ui-library/Colors';
import {FloatingPlacement, FloatingInteraction} from 'ui-library/floating-ui/FloatingConstants';
import Popover from 'ui-library/floating-ui/Popover';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {LabelStyle} from 'ui-library/inputs/InputConstants';
import * as keyboard from 'util/Keyboard';
import {ElementKeyboardEvent} from 'util/Types';

export interface ValidationInfo {
  name: string;
  isValid?: boolean;
  validationMsg?: string;
}

interface EditableSchemaTagProps {
  itemLinkAllowed: boolean;
  fileId?: string;
  isWritable: boolean;
  labelClassName?: string;
  labelStyle?: LabelStyle;
  schemaFieldSelectorId?: string;
  sequenceParts?: Array<PartJSON>;
  tag: SchemaTag;
  tagPopover?: string | null;
  tagValidationInfo?: ValidationInfo | null;
  // Disable and render provided tooltip if non-null
  disableEditsWithTooltip?: string | null;
  onUpdateTag: (tag: SchemaTag) => void;
  // Wait for confirmation from this function before entering edit mode
  confirmBeforeStartEditing?: () => Promise<boolean>;
  customEditor?: (
    tag: SchemaTag,
    autoFocus: boolean,
    onChange: (newValue: Partial<SchemaTag>) => void
  ) => JSX.Element;
}

interface State {
  isBeingEdited: boolean;
  tag: SchemaTag;
}

interface ValidationInfoPopoverProps {
  validationInfo: ValidationInfo;
}

export function ValidationInfoPopover(props: ValidationInfoPopoverProps): JSX.Element {
  const validationMsg: string =
    props.validationInfo.isValid !== undefined
      ? props.validationInfo.isValid
        ? 'Valid'
        : props.validationInfo.validationMsg!
      : 'Pending validation';
  const style =
    props.validationInfo.isValid !== undefined
      ? props.validationInfo.isValid
        ? 'valid'
        : 'invalid'
      : 'unknown';
  return (
    <Popover
      interaction={FloatingInteraction.HOVER}
      renderContent={() => validationMsg}
      placement={FloatingPlacement.LEFT}
    >
      <BenchlingIcon className={css(styles.statusIcon, styles[style])} icon="circle-small" />
    </Popover>
  );
}

/**
 * A component that handles editing a schema tag.
 *
 * Renders the tag value when not editing and a corresponding input when editing. Includes complete and
 * cancel buttons for saving the edit.
 */
class EditableSchemaTag extends React.Component<EditableSchemaTagProps, State> {
  static propTypes = {
    itemLinkAllowed: PropTypes.bool.isRequired,
    fileId: PropTypes.string,
    isWritable: PropTypes.bool.isRequired,
    labelClassName: PropTypes.string,
    labelStyle: PropTypes.oneOf(Object.values(FieldLabel.STYLES)),
    schemaFieldSelectorId: PropTypes.string,
    sequenceParts: PropTypes.array,
    tag: PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.string,
      tagSchemaField: PropTypes.shape({
        isConvertedFromLink: PropTypes.bool,
        isComputed: PropTypes.bool,
        isSnapshot: PropTypes.bool,
        isUneditable: PropTypes.bool,
      }),
    }).isRequired,
    onUpdateTag: PropTypes.func.isRequired, // called with (tag)
    confirmBeforeStartEditing: PropTypes.func,
    disableEditsWithTooltip: PropTypes.string,
    tagPopover: PropTypes.string,
    customEditor: PropTypes.func,
  };

  static defaultProps = {isWritable: true};

  state: State = {
    isBeingEdited: false,
    tag: this.props.tag,
  };

  UNSAFE_componentWillReceiveProps(nextProps: EditableSchemaTagProps): void {
    if (!this.state.isBeingEdited) {
      this.setState({tag: nextProps.tag});
    }
  }

  handleChangeValue = (newValue: Partial<SchemaTag>): void => {
    this.setState({tag: reactUpdate(this.state.tag, {$merge: newValue})});
  };

  handleKeyDown = (e: ElementKeyboardEvent): void => {
    if (keyboard.isEnterKey(e)) {
      e.preventDefault();
      this.handleComplete();
    } else if (keyboard.isEscapeKey(e)) {
      e.preventDefault();
      this.handleCancel();
    }
  };

  handleCancel = (): void => {
    this.setState({
      tag: this.props.tag,
      isBeingEdited: false,
    });
  };

  handleComplete = (): void => {
    if (!TagUtil.areTagsValidAndNotify([this.state.tag])) {
      return;
    }
    this.props.onUpdateTag(this.state.tag);

    this.setState({
      isBeingEdited: false,
      // Reset the state to the given tag - onUpdateTag should update it
      tag: this.props.tag,
    });
  };

  handleEdit = (): void => {
    this.setState({isBeingEdited: true});
  };

  handleEditWithConfirmation = async (): Promise<void> => {
    assert(this.props.confirmBeforeStartEditing != null, 'Expected confirmBeforeStartEditing to be provided');
    const shouldProceed = await this.props.confirmBeforeStartEditing();
    if (shouldProceed) {
      this.setState({isBeingEdited: true});
    }
  };

  render(): JSX.Element {
    const isWritable =
      this.props.isWritable &&
      this.props.disableEditsWithTooltip == null &&
      !SchemaFieldUtil.isUneditableField(this.state.tag.tagSchemaField);

    const editor = this.props.customEditor ? (
      this.props.customEditor(this.state.tag, this.state.isBeingEdited, this.handleChangeValue)
    ) : (
      <TagValueEditor
        autoFocus={this.state.isBeingEdited}
        fileId={this.props.fileId}
        itemLinkAllowed={this.props.itemLinkAllowed}
        onChange={this.handleChangeValue}
        onKeyDown={this.handleKeyDown}
        schemaFieldSelectorId={this.props.schemaFieldSelectorId}
        sequenceParts={this.props.sequenceParts}
        tag={this.state.tag}
      />
    );
    const editableField = (
      <BaseEditableField
        editor={editor}
        isBeingEdited={this.state.isBeingEdited}
        isCompleteEnabled={true}
        isWritable={isWritable}
        viewer={<TagValueViewer tag={this.state.tag} />}
        onCancel={this.handleCancel}
        onComplete={this.handleComplete}
        onEdit={
          this.props.confirmBeforeStartEditing != null ? this.handleEditWithConfirmation : this.handleEdit
        }
      />
    );
    return (
      <div {...getTestProps({component: 'EditableSchemaTag', element: 'root', key: this.state.tag.name})}>
        <FieldLabel
          className={this.props.labelClassName}
          label={this.state.tag.tagSchemaField.displayName}
          labelStyle={this.props.labelStyle}
          dataTestAttrs={{component: 'EditableSchemaTag', element: 'label'}}
        />
        {this.props.tagValidationInfo && (
          <ValidationInfoPopover validationInfo={this.props.tagValidationInfo} />
        )}
        {this.props.tagPopover && (
          <HelpButton renderContent={() => this.props.tagPopover} iconClassName="u-mini-spacing-left" />
        )}
        <Tooltip text={this.props.disableEditsWithTooltip} isDisabled={!this.props.disableEditsWithTooltip}>
          <div {...getTestProps({component: 'EditableSchemaTag', element: 'value'})}>{editableField}</div>
        </Tooltip>
      </div>
    );
  }
}

const styles = StyleSheet.create({
  statusIcon: {
    marginLeft: '4px',
  },
  invalid: {
    color: Colors.RED_LIGHT,
  },
  unknown: {
    color: Colors.ORANGE,
  },
  valid: {
    color: Colors.GREEN,
  },
});

export default EditableSchemaTag;
